import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Rocket, AlertTriangle, Receipt, ExternalLink, Wallet, Gift, Sparkles,
  Check, Clock, TrendingDown,
} from "lucide-react";

const TIER_META = {
  starter: {
    tagline: "For Your First Rental",
    blurb: "Everything you need to list, lease and get paid for your first property.",
    features: [
      "1 active listing",
      "Online applications & tenant screening",
      "E-sign leases & disclosures",
      "Card, PayPal & Venmo rent collection",
      "Maintenance requests & messaging",
      "Automated rent reminders",
    ],
  },
  growth: {
    tagline: "For Growing Portfolios",
    blurb: "Scale up to 10 units and pay a lower platform fee as your portfolio grows.",
    features: [
      "Up to 10 active listings",
      "Everything in Starter",
      "Lower 2% platform fee on rent",
      "Showing scheduling & negotiation tools",
      "Contractor address book & dispatch",
    ],
  },
  pro: {
    tagline: "For Professional Investors",
    blurb: "Unlimited units, the lowest fee, plus priority support and the reporting & AI suites.",
    features: [
      "Unlimited listings",
      "Everything in Growth",
      "Lowest 1% platform fee on rent",
      "0% platform fee on Venmo rent",
      "Priority support queue & faster responses",
      "Premium Reporting Suite (coming soon)",
      "Pro AI Suite (coming soon)",
    ],
  },
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

export const PayoutsTab = ({
  payout, billing, paypalEmail, setPaypalEmail, venmoHandle, setVenmoHandle, busy,
  upgradePlan, cancelPlan, reactivatePlan, savePayout, connectStripe, fmtDate,
  couponCode, setCouponCode, redeemCoupon,
}) => {
  const [interval, setInterval] = useState(payout.billing_interval === "year" ? "year" : "month");
  const tiers = payout.tiers || [];
  const current = payout.plan_tier || "starter";
  const status = payout.plan_status || "trialing";
  const isTrialing = status === "trialing";
  const isActive = status === "active";
  const isCanceling = status === "canceling" || payout.cancel_at_period_end;
  const isComped = status === "comped";
  const isOnHold = status === "past_due" || payout.fee_suspended;
  const trialDays = daysLeft(payout.trial_end);
  const showRedeem = !isComped || payout.comp_type === "free_month";
  const pct = (v) => `${Number(v ?? 0)}%`;

  const tierLabel = (k) => (k ? k.charAt(0).toUpperCase() + k.slice(1) : "");
  const priceOf = (t) => (interval === "year" ? t.price_year : t.price_month);

  return (
  <div className="max-w-3xl space-y-5">
    {/* Status banners */}
    {isTrialing && (
      <div data-testid="trial-banner" className="rounded-xl border border-forest/25 bg-forest/[0.04] p-4 flex items-start gap-3">
        <Clock className="h-5 w-5 text-forest mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-ink">You're on a 14-day free Starter trial{trialDays != null && <> — <span className="text-forest font-semibold">{trialDays} day{trialDays === 1 ? "" : "s"} left</span></>}.</p>
          <p className="text-xs text-mute mt-0.5">Your trial ends {payout.trial_end ? fmtDate(payout.trial_end) : "soon"}. Subscribe to any plan below to keep your account active — no charge until you subscribe.</p>
        </div>
      </div>
    )}
    {isOnHold && (
      <div data-testid="onhold-banner" className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-800">Your account is on hold.</p>
          <p className="text-xs text-amber-700 mt-0.5">Your trial or subscription has ended. Your listings stay live, but you can't make changes until you subscribe to a plan below.</p>
        </div>
      </div>
    )}

    {/* Plan & pricing */}
    <div data-testid="plan-card" className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Rocket className="h-5 w-5 text-forest" /><h2 className="text-lg font-heading font-medium text-ink">Your Plan</h2></div>
        <span data-testid="plan-badge" className="text-xs font-semibold text-forest bg-forest/5 border border-forest/20 rounded-full px-2.5 py-0.5">
          {tierLabel(current)}{isComped ? " · Comp" : isTrialing ? " · Trial" : isCanceling ? " · Canceling" : isOnHold ? " · On hold" : ""}
        </span>
      </div>
      <p className="text-sm text-mute mt-1">
        <span data-testid="listing-usage" className="font-medium text-ink">You have {payout.listing_count} listing{payout.listing_count === 1 ? "" : "s"}</span>
        {payout.listing_limit != null ? ` of ${payout.listing_limit} allowed on ${tierLabel(current)}.` : " · unlimited listings."}
        {" "}Platform fee: <span className="font-medium text-ink">{pct(payout.platform_fee_percent)}</span> on rent &amp; deposits.
      </p>

      {/* Marketing headline */}
      <div className="mt-5 flex items-center gap-2">
        <TrendingDown className="h-5 w-5 text-forest" />
        <p className="text-base md:text-lg font-heading font-medium text-ink">The more your portfolio grows, the less you pay.</p>
      </div>
      <p className="text-sm text-mute mt-1">Every plan includes online applications, e-signed leases, and card/PayPal/Venmo rent collection. Upgrade to unlock more listings and a lower platform fee on every rent &amp; deposit payment.</p>

      {/* Monthly / Annual toggle */}
      <div className="mt-4 inline-flex items-center rounded-full border border-line bg-stone-50 p-1" data-testid="interval-toggle">
        <button
          data-testid="interval-month"
          onClick={() => setInterval("month")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${interval === "month" ? "bg-forest text-white" : "text-mute hover:text-ink"}`}
        >Monthly</button>
        <button
          data-testid="interval-year"
          onClick={() => setInterval("year")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${interval === "year" ? "bg-forest text-white" : "text-mute hover:text-ink"}`}
        >Annual <span className="text-[11px] opacity-80">· save more</span></button>
      </div>

      {/* Tier cards */}
      <div className="mt-4 grid md:grid-cols-3 gap-3">
        {tiers.map((t) => {
          const meta = TIER_META[t.key] || {};
          const isCurrent = current === t.key;
          const price = priceOf(t);
          const canManage = isCurrent && isActive;
          return (
            <div key={t.key} data-testid={`tier-card-${t.key}`} className={`rounded-xl p-4 border flex flex-col ${isCurrent ? "border-forest bg-forest/[0.04]" : "border-line"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-heading font-semibold text-ink">{tierLabel(t.key)}</p>
                {isCurrent && <span data-testid={`tier-current-${t.key}`} className="text-[10px] font-semibold text-forest bg-forest/10 border border-forest/20 rounded-full px-2 py-0.5">Current</span>}
              </div>
              <p className="text-[11px] font-medium text-forest uppercase tracking-wide mt-0.5">{meta.tagline}</p>
              <p className="text-2xl font-heading font-semibold text-ink mt-2">
                ${Number(price ?? 0).toFixed(2)}<span className="text-xs font-normal text-mute">/{interval === "year" ? "yr" : "mo"}</span>
              </p>
              <div className="mt-2 rounded-lg bg-white border border-line px-2.5 py-1.5">
                <p className="text-[11px] text-mute">Platform fee on rent &amp; deposits</p>
                <p data-testid={`tier-fee-${t.key}`} className="text-base font-heading font-semibold text-forest leading-tight">{pct(t.fee)}</p>
              </div>
              <p className="text-xs text-mute mt-2">{meta.blurb}</p>
              <ul className="mt-3 space-y-1.5 flex-1">
                {(meta.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-1.5 text-[12px] text-ink"><Check className="h-3.5 w-3.5 text-forest mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-4">
                {canManage ? (
                  isCanceling ? (
                    <button data-testid="reactivate-plan-btn" onClick={reactivatePlan} className="w-full bg-forest text-white py-2 rounded-lg font-medium text-sm hover:bg-forest-hover transition-colors">Reactivate plan</button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid="cancel-plan-btn" className="w-full border border-red-200 text-red-600 py-2 rounded-lg text-sm font-medium hover:bg-red-50">Cancel plan</button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="cancel-plan-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel your {tierLabel(t.key)} plan?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Cancellation is <span className="font-semibold text-ink">non-refundable</span>. Your plan stays active until the end of the current billing period{payout.current_period_end ? ` (${fmtDate(payout.current_period_end)})` : ""}. After that your account goes on hold until you subscribe again — your listings stay live but become read-only. You can reactivate anytime before the period ends.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-plan-dismiss">Keep my plan</AlertDialogCancel>
                          <AlertDialogAction data-testid="cancel-plan-confirm" onClick={cancelPlan} className="bg-red-600 hover:bg-red-700">Cancel plan</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                ) : isComped && t.key === "pro" ? (
                  <p className="text-center text-xs font-medium text-forest bg-forest/10 border border-forest/15 rounded-lg py-2">Complimentary access</p>
                ) : (
                  <button
                    data-testid={`subscribe-${t.key}-btn`}
                    onClick={() => upgradePlan(t.key, interval)}
                    className="w-full bg-forest text-white py-2 rounded-lg font-medium text-sm hover:bg-forest-hover transition-colors"
                  >
                    {isTrialing || isOnHold ? "Subscribe" : isCurrent ? `Switch to ${interval === "year" ? "Annual" : "Monthly"}` : "Switch"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p data-testid="venmo-fee-note" className="text-xs text-mute mt-3">
        Venmo & manual rent: a platform fee is accrued and billed monthly — <span className="font-medium text-ink">Starter 4%</span>, <span className="font-medium text-ink">Growth 2%</span>, <span className="font-medium text-ink">Pro 0% (exempt)</span>. Application fees always go to the platform.
      </p>

      {isComped && (
        <div data-testid="comped-banner" className="mt-4 rounded-lg border border-forest/25 bg-forest/[0.04] p-4 flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-forest mt-0.5 shrink-0" />
          <p className="text-sm text-ink">
            {payout.comp_type === "master"
              ? <>You have <span className="font-semibold">Master Pro</span> access — unlimited listings, no subscription charge, and <span className="font-semibold">0% platform fees</span> on rent &amp; deposits (including Venmo &amp; manual payments). The platform is completely free for you.</>
              : payout.comp_type === "free_month"
              ? <>Your free month of Pro is active{payout.current_period_end ? <> until <span className="font-semibold">{fmtDate(payout.current_period_end)}</span></> : ""}. You're still charged the standard platform % fee on rent &amp; deposits.</>
              : "You have complimentary Pro access — unlimited listings, no subscription charge. You're still charged the standard platform % fee on rent & deposits."}
          </p>
        </div>
      )}

      {showRedeem && (
        <div data-testid="coupon-redeem" className="mt-4 border-t border-line pt-4">
          <p className="text-sm font-medium text-ink flex items-center gap-1.5"><Gift className="h-4 w-4 text-forest" /> Have a coupon code?</p>
          <p className="text-xs text-mute mt-0.5">Redeem a free-month or free-Pro code from the platform admin.</p>
          <div className="mt-2 flex gap-2">
            <input data-testid="coupon-input" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. PRO-XXXX-XXXX" className="flex-1 border border-line rounded-lg px-3 py-2 text-sm uppercase" />
            <button data-testid="coupon-redeem-btn" disabled={!couponCode.trim()} onClick={redeemCoupon} className="bg-forest text-white px-5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60">Redeem</button>
          </div>
        </div>
      )}
    </div>

    {/* Billing history */}
    <div data-testid="billing-history-card" className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center gap-2"><Receipt className="h-5 w-5 text-forest" /><h2 className="text-lg font-heading font-medium text-ink">Billing History</h2></div>
      <p className="text-sm text-mute mt-1">Your subscription charges. Deposits and rent are paid to your own accounts and aren't shown here.</p>
      {billing.length === 0 ? (
        <p data-testid="billing-empty" className="text-sm text-mute mt-4">No subscription charges yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-line border border-line rounded-lg overflow-hidden">
          {billing.map((b) => (
            <div key={b.id} data-testid="billing-row" className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink truncate">{b.description}</p>
                <p className="text-xs text-mute">{b.date ? fmtDate(b.date) : "—"}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${b.status === "paid" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{b.status}</span>
                <span className="text-sm font-medium text-ink tabular-nums">${Number(b.amount).toFixed(2)}</span>
                {b.receipt_url && (
                  <a data-testid="billing-receipt-link" href={b.receipt_url} target="_blank" rel="noreferrer" className="text-mute hover:text-forest transition-colors" title="View invoice"><ExternalLink className="h-4 w-4" /></a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    <div className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center gap-2"><Wallet className="h-5 w-5 text-forest" /><h2 className="text-lg font-heading font-medium text-ink">Your Payout Accounts</h2></div>
      <p className="text-sm text-mute mt-1">Security deposits and monthly rent are paid directly into your own accounts. A platform fee of <span className="font-medium text-ink">{pct(payout.platform_fee_percent)}</span> applies. Application fees go to the platform.</p>
    </div>

    <div className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-ink">PayPal</h3>
        {payout.paypal_email ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">Connected</span> : <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">Not set</span>}
      </div>
      <p className="text-sm text-mute mt-1">Enter your PayPal business email — renters paying by PayPal/Venmo will send funds directly to you.</p>
      <div className="mt-3 flex gap-2">
        <Input data-testid="payout-paypal-email" type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} placeholder="you@yourbusiness.com" className="border-line" />
        <button data-testid="save-payout-btn" disabled={busy} onClick={savePayout} className="bg-forest text-white px-5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60">Save</button>
      </div>
    </div>

    <div className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-ink">Venmo (pay rent by link)</h3>
        {venmoHandle ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">Set</span> : <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">Not set</span>}
      </div>
      <p className="text-sm text-mute mt-1">Enter your Venmo username. Tenants get a one-tap "Pay with Venmo" button on their dashboard with the rent amount and month pre-filled. Payments go straight to your Venmo — record them with "Mark rent as paid" once received.</p>
      <div className="mt-3 flex gap-2 items-center">
        <div className="flex items-center flex-1 border border-line rounded-md px-3 focus-within:ring-2 focus-within:ring-forest/20 bg-white">
          <span className="text-mute text-sm">@</span>
          <input data-testid="payout-venmo-handle" value={venmoHandle} onChange={(e) => setVenmoHandle(e.target.value.replace(/^@+/, ""))} placeholder="your-venmo-username" className="flex-1 px-2 py-2 text-sm focus:outline-none bg-transparent" />
        </div>
        <button data-testid="save-venmo-btn" disabled={busy} onClick={savePayout} className="bg-forest text-white px-5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60">Save</button>
      </div>
    </div>

    <div className="bg-white border border-line rounded-xl p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-ink">Stripe (Card & ACH to your bank)</h3>
        {payout.stripe_onboarded ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">Onboarded</span> : <span className="text-xs font-semibold text-stone-600 bg-stone-100 border border-stone-200 rounded-full px-2.5 py-0.5">Not connected</span>}
      </div>
      <p className="text-sm text-mute mt-1">Connect a Stripe account to receive card/ACH rent into your bank. Requires the platform's real Stripe key to be configured.</p>
      <button data-testid="connect-stripe-btn" onClick={connectStripe} className="mt-3 border border-forest text-forest px-5 py-2 rounded-lg font-medium hover:bg-forest/5">Connect with Stripe</button>
    </div>
  </div>
  );
};
